import React, { useState, useRef } from 'react';
import { Button } from 'aha-components';
import { isMobile, getDebugger } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { UploadIcon, DeleteIcon, WarnRedIcon } from '@/components/common/icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import axiosInstance from '@/apis/axiosInstance';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import axios from 'axios';
import toast from 'react-hot-toast';
import ModalAnalysisAudienceTutorialScreenshot from './ModalAnalysisAudienceTutorialScreenshot';
import { useTranslation } from 'react-i18next';

const ALLOWED_IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp';
const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const isAllowedImageFile = (file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  return (
    ALLOWED_IMAGE_MIME_TYPES.includes(file.type) ||
    ALLOWED_IMAGE_EXTENSIONS.includes(extension)
  );
};

const ModalAnalysisUploadScreenshotsContent: React.FC<{
  setShowUploadScreenshotsComponent: (v: boolean) => void;
  onAnalysisSuccess?: (detailData?: any) => void;
  onAnalysisCancel?: () => void;
}> = ({
  setShowUploadScreenshotsComponent,
  onAnalysisSuccess,
  onAnalysisCancel
}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const {
    setShowModalAnalysis,
    setShowModalConfirmDealPrice,
    setModalNotMatchInfo
  } = useWebModalStore();
  const uploadedImageListRef = useRef<any[]>([]);
  const dragCounterRef = useRef(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isOCRing, setIsOCRing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [, forceUpdate] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const handleNextToOCR = async () => {
    setIsOCRing(true);
    // influencer/:platform/audience/imageLink
    const platform = detailData?.order?.platform;
    const imageLink = uploadedImageListRef.current
      .filter((item) => item.uploadStatus === 'success')
      .map((item) => item.url);
    try {
      const {
        data: { data: responseData }
      } = await axiosInstance.post(
        `/influencer/${platform}/audience/imageLink`,
        {
          urls: imageLink,
          orderId: detailData?.order?.id
        }
      );
      enum CHECK_AUDIENCE_ERROR {
        // 识别失败
        PARSE_FAILED = 'parse_failed',
        // linkedin缺少数据
        MISSING_DATA_LINKEDIN = 'missing_data_linkedin',
        // 非linkedin渠道缺少数据
        MISSING_DATA = 'missing_data',
        // 缺少频道信息
        MISSING_CHANNEL = 'missing_channel',
        // 不匹配
        NOT_MATCH = 'not_match'
      }
      let errorMessage = '';
      if (responseData?.success) {
        // 成功
        const nextDetailData = await reQueryDetailData({
          orderId: detailData?.order?.id
        });
        if (onAnalysisSuccess) {
          onAnalysisSuccess(nextDetailData);
        } else {
          setShowModalAnalysis(false);
          setShowModalConfirmDealPrice(true);
        }
      } else if (!responseData?.success && responseData?.message) {
        switch (responseData?.message) {
          case CHECK_AUDIENCE_ERROR.PARSE_FAILED:
            errorMessage = t('analysis.upload.error.parse_failed', {
              defaultValue:
                'Screenshots not recognized. Please submit the images again.'
            });
            break;
          case CHECK_AUDIENCE_ERROR.MISSING_DATA_LINKEDIN:
            errorMessage = t('analysis.upload.error.missing_data_linkedin', {
              defaultValue:
                'Please ensure your screenshots include the viewer location.'
            });
            break;
          case CHECK_AUDIENCE_ERROR.MISSING_DATA:
            errorMessage = t('analysis.upload.error.missing_data', {
              defaultValue:
                'Please ensure your screenshots display all audience dimensions, including geography, age, and gender.'
            });
            break;
          case CHECK_AUDIENCE_ERROR.MISSING_CHANNEL:
            errorMessage = t('analysis.upload.error.missing_channel', {
              defaultValue:
                'Please make sure your channel name ({{name}}) appears in the screenshots.',
              name: detailData?.influencer?.name
            });
            break;
          // 达人受众不匹配
          case CHECK_AUDIENCE_ERROR.NOT_MATCH:
            if (onAnalysisCancel) {
              onAnalysisCancel();
            } else {
              setShowModalAnalysis(false);
            }
            setModalNotMatchInfo({
              visible: true,
              message: responseData?.detail
            });
            return;
          default:
            errorMessage = t('analysis.upload.error.unknown', {
              defaultValue:
                'Failed to get audience data: {{message}} For further inquiries, please take a screenshot of this page and contact support team.',
              message: responseData?.message
            });
        }
        setErrorMessage(errorMessage);
        toast.error(errorMessage);
      } else {
        toast.error(
          t('analysis.upload.error.unknown_error', {
            defaultValue: 'unknown error'
          })
        );
      }
      console.log('responseData', responseData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsOCRing(false);
    }
  };

  const queryUploadedImageList = async (imageList: any[]) => {
    setIsUploading(true);
    try {
      // 保留之前的状态，只将新文件添加为 pending 状态
      const newImageList = [
        ...uploadedImageListRef.current,
        ...imageList.map((item) => ({
          ...item,
          uploadStatus: 'pending'
        }))
      ];
      uploadedImageListRef.current = newImageList;
      forceUpdate({});

      const {
        data: { data: responseData }
      } = await axiosInstance.post('/influencer/uploadLinkBatch/public', {
        keys: imageList?.map((item) => item.name)
      });

      if (!responseData?.length) {
        toast.error('batch upload failed.');
        return;
      }

      // 使用 presignedUrl 上传文件
      const uploadPromises = responseData.map(
        async (item: any, index: number) => {
          const file = imageList[index].file;
          const presignedUrl = item.presignedUrl;
          const publicUrl = item.publicUrl;
          try {
            const options = {
              headers: {
                'Content-Type': file.type
              }
            };
            await axios.put(presignedUrl, file, options);
            return {
              ...imageList[index],
              url: publicUrl,
              uploadStatus: 'success'
            };
          } catch (error) {
            console.error('Upload error:', error);
            return {
              ...imageList[index],
              uploadStatus: 'error',
              error: error
            };
          }
        }
      );

      const results = await Promise.all(uploadPromises);

      // 更新状态时，只更新新上传的文件的状态
      const currentFiles = uploadedImageListRef.current;
      const updatedFiles = currentFiles.map((file) => {
        const matchingResult = results.find(
          (result) => result.name === file.name
        );
        return matchingResult || file;
      });

      uploadedImageListRef.current = updatedFiles;
      forceUpdate({});
    } catch (error) {
      console.error(error);
      // 错误时也只更新新上传文件的状态
      const currentFiles = uploadedImageListRef.current;
      const updatedFiles = currentFiles.map((file) => {
        const matchingFile = imageList.find((img) => img.name === file.name);
        return matchingFile
          ? {
              ...file,
              uploadStatus: 'error',
              error
            }
          : file;
      });

      uploadedImageListRef.current = updatedFiles;
      forceUpdate({});
    } finally {
      setIsUploading(false);
    }
  };

  const processFiles = (files: File[]) => {
    if (files.length === 0) return;

    const validFiles = files.filter(isAllowedImageFile);
    if (validFiles.length < files.length) {
      toast.error(
        t('analysis.upload.error.invalid_format', {
          defaultValue: 'Only JPG, PNG, and WEBP images are supported.'
        })
      );
    }
    if (validFiles.length === 0) return;

    const imageList = validFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
      file
    }));
    queryUploadedImageList(imageList);
  };

  const handleUploadImage = (e: any) => {
    const files: FileList = e.target.files;
    if (files) {
      processFiles(Array.from(files));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };
  return (
    <div className="relative w-full flex flex-col items-start gap-6 flex-1 min-h-0">
      <div className="shrink-0 w-full">
        <ModalAnalysisBreadcrumb index={0} />
      </div>
      <div className="w-full flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
        <div className="self-stretch justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
          {t('analysis.upload.title', { defaultValue: 'Upload screenshots' })}
        </div>
        {/*  */}
        <input
          accept={ALLOWED_IMAGE_ACCEPT}
          id="contained-button-file"
          multiple
          type="file"
          style={{ display: 'none' }}
          onChange={handleUploadImage}
        />
        <label htmlFor="contained-button-file" className="w-full">
          <div
            className={`w-full self-stretch px-3 py-[22px] bg-white rounded-md border border-dashed flex justify-center items-center gap-1
          hover:border-theme-color cursor-pointer group/uploadButton
          ${isDragging ? 'border-theme-color bg-[#f5f3ff]' : 'border-[#d9d9e0]'}
          `}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <CircularProgress size={18} />
            ) : (
              <UploadIcon className="w-5 h-5 group-hover/uploadButton:text-[#5A31F0]" />
            )}
            <div className="w-0 h-5" />
            <div className="justify-start text-[#1c2024] text-sm font-medium font-['Inter'] group-hover/uploadButton:text-[#5A31F0]">
              {isUploading
                ? t('analysis.upload.status.uploading', {
                    defaultValue: 'Uploading...'
                  })
                : t('analysis.upload.action.upload', {
                    defaultValue: 'Upload'
                  })}
            </div>
          </div>
        </label>
        {/*  */}
        <div className="w-full px-5 flex flex-col gap-2">
          {isOCRing && (
            <div className="inline-flex justify-start items-center gap-2">
              {/* mui 加载动画 */}
              <CircularProgress size={14} />
              <div className="justify-start text-[#8b8d98] text-sm font-normal font-['Inter'] leading-tight">
                {t('analysis.upload.status.recognizing', {
                  defaultValue: 'Recognizing your audience data. Please wait...'
                })}
              </div>
            </div>
          )}
          <div
            className={`w-full h-[1px] bg-[#e8e8ec] my-3 
              ${isOCRing ? 'block' : 'hidden'}
            `}
          />
          {/* image list */}
          <UploadedImageListComponent
            imageList={uploadedImageListRef.current}
            setImageList={(newList) => {
              uploadedImageListRef.current = newList;
              forceUpdate({});
            }}
          />
          {/*  */}
          {errorMessage && (
            <div className="mt-4 self-stretch px-2 py-1 bg-[#ffebe5] rounded inline-flex justify-center items-center gap-1">
              <WarnRedIcon className="w-[14px] h-[14px]" />
              <div className="flex-1 justify-start text-[#d43710] text-xs font-normal font-['Inter'] leading-tight">
                {errorMessage}
              </div>
            </div>
          )}
        </div>
        {/*  */}
        <ModalAnalysisAudienceTutorialScreenshot />
      </div>
      {/*  */}
      <div
        className={`bg-white w-full flex items-center gap-3 border-t border-solid border-[#e8e8ec] pt-4 shrink-0
      ${isMobile() ? 'justify-center pb-[calc(16px+env(safe-area-inset-bottom,0px))]' : 'justify-end'}
      `}
      >
        <Button
          className="!w-[160px] !h-10"
          type="default"
          onClick={() => {
            setShowUploadScreenshotsComponent(false);
          }}
        >
          {t('analysis.upload.action.back', { defaultValue: 'Back' })}
        </Button>
        <Button
          className="!w-[160px] !h-10"
          type="primary"
          disabled={
            getDebugger() === 'true'
              ? false
              : !uploadedImageListRef.current.length ||
                uploadedImageListRef.current?.find(
                  (item) =>
                    item.uploadStatus === 'error' ||
                    item.uploadStatus === 'pending'
                )
          }
          onClick={() => {
            // 图片识别校验接口。
            handleNextToOCR();
          }}
          loading={isOCRing}
        >
          {t('analysis.upload.action.next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </div>
  );
};

export default ModalAnalysisUploadScreenshotsContent;

const UploadedImageListComponent = ({
  imageList,
  setImageList
}: {
  imageList: any[];
  setImageList: (imageList: any[]) => void;
}) => {
  const { t } = useTranslation();
  const { setShowModalShowImage } = useWebModalStore();
  return (
    <div className="flex flex-col justify-start items-start gap-2">
      {imageList.map((item) => {
        return (
          <div
            key={item.name}
            className="w-full inline-flex justify-center items-center gap-2.5"
          >
            <img
              src={item.url}
              alt={item.name}
              className="w-5 h-5 cursor-pointer"
              onClick={() => {
                setShowModalShowImage(true, item.url);
              }}
            />
            <div
              className="flex-1 justify-start text-[#60646c] text-xs font-medium font-['Inter']"
              onClick={() => {
                setShowModalShowImage(true, item.url);
              }}
            >
              {item.name}
            </div>
            {item.uploadStatus === 'success' && (
              <div className="text-xs font-medium font-['Inter']">
                {t('analysis.upload.status.success', {
                  defaultValue: 'Success'
                })}
              </div>
            )}
            {item.uploadStatus === 'error' && (
              <div className="text-xs font-medium font-['Inter'] text-[#FF4500]">
                {t('analysis.upload.status.error', { defaultValue: 'Error' })}
              </div>
            )}
            {item.uploadStatus === 'pending' && (
              <div className="text-xs font-medium font-['Inter']">
                {t('analysis.upload.status.uploading', {
                  defaultValue: 'Uploading...'
                })}
              </div>
            )}
            <div
              className=""
              onClick={() => {
                setImageList(
                  imageList.filter((image) => image.name !== item.name)
                );
              }}
            >
              <DeleteIcon className="w-4 h-4 cursor-pointer text-[#60646C] hover:text-[#5A31F0]" />
            </div>
          </div>
        );
      })}
    </div>
  );
};
